/**
 * Additional taxes (ENG additional-taxes): a second tax on part of the menu, e.g. the
 * Oklahoma 13.5% Mixed Beverage Tax on alcohol. One store can define several; each applies
 * to menu categories (menu groups) and/or individual items (menu item groups), with a
 * per-item opt-out from a category rule. See docs/additional-taxes/product-spec.md.
 *
 * Backend: /backend/v2/additional_tax/ (app `additional_tax`).
 */

/** A tax as the settings endpoints return it (GET store/<id>/, add, update). */
export interface AdditionalTax {
  id: number;
  name: string;
  /** Receipt label. Blank means "<name> <rate>%" is printed. */
  label: string;
  /** Decimal string with four places, e.g. "13.5000". */
  rate_percent: string;
  report_code: string;
  applies_to_modifiers: boolean;
  is_active: boolean;
  sort_order: number;
  category_ids: number[];
  menu_item_group_ids: number[];
  excluded_menu_item_group_ids: number[];
}

/** Body of add/ and update/ (update is partial). */
export interface AdditionalTaxInput {
  name: string;
  label?: string;
  rate_percent: string | number;
  report_code?: string;
  applies_to_modifiers?: boolean;
  is_active?: boolean;
  category_ids?: number[];
  menu_item_group_ids?: number[];
  excluded_menu_item_group_ids?: number[];
}

/** The slimmer copy on the store payload (`store.additional_taxes`, active taxes only). */
export interface AdditionalTaxSummary {
  id: number;
  name: string;
  label: string;
  rate_percent: string;
  applies_to_modifiers: boolean;
  category_ids: number[];
  menu_item_group_ids: number[];
  excluded_menu_item_group_ids: number[];
}

/** GET store/<id>/menu_map/ — effective tax ids per menu item group and per menu group. */
export interface AdditionalTaxMenuMap {
  items: { [menuItemGroupId: string]: number[] };
  categories: { [menuGroupId: string]: number[] };
}

/** One line of `payment.additional_taxes` on an order (rate and label are snapshots). */
export interface OrderAdditionalTax {
  id: number;
  name: string;
  label: string;
  rate_percent: string | number;
  taxable_base: string | number;
  amount: string | number;
  cash_amount: string | number;
}

/** One row of GET store/<id>/report/. */
export interface AdditionalTaxReportRow {
  id: number;
  name: string;
  label: string;
  rate_percent: string;
  order_count: number;
  taxable_base: string;
  amount: string;
  cash_amount: string;
  card_amount: string;
  pos_amount: string;
  online_amount: string;
}

export interface AdditionalTaxReport {
  taxes: AdditionalTaxReportRow[];
  total_amount: string;
  time: any;
}

/** "13.5000" -> "13.5", "8.6250" -> "8.625", 7 -> "7". */
export function formatTaxRate(rate: string | number): string {
  const n = typeof rate === 'number' ? rate : parseFloat(rate);
  if (!isFinite(n)) { return '0'; }
  return n.toFixed(4).replace(/\.?0+$/, '');
}

/** The receipt label a tax prints with: its own label, else "<name> <rate>%". */
export function additionalTaxLabel(tax: { name: string; label?: string; rate_percent: string | number }): string {
  if (tax.label && tax.label.trim()) { return tax.label.trim(); }
  return `${tax.name} ${formatTaxRate(tax.rate_percent)}%`;
}

/**
 * Resolves which taxes apply to one menu item group from the store's assignments
 * (R3 in the spec): assigned directly, or its category is assigned and the item is not
 * excluded. `menuGroupId` may be unknown (null), in which case only direct assignments
 * count.
 */
export function resolveAdditionalTaxIds(
  taxes: Array<Pick<AdditionalTaxSummary, 'id' | 'category_ids' | 'menu_item_group_ids' | 'excluded_menu_item_group_ids'>>,
  menuItemGroupId: number | null | undefined,
  menuGroupId: number | null | undefined
): number[] {
  const out: number[] = [];
  for (const tax of taxes || []) {
    const direct = menuItemGroupId != null && (tax.menu_item_group_ids || []).some(id => +id === +menuItemGroupId);
    const excluded = menuItemGroupId != null && (tax.excluded_menu_item_group_ids || []).some(id => +id === +menuItemGroupId);
    const byCategory = menuGroupId != null && (tax.category_ids || []).some(id => +id === +menuGroupId) && !excluded;
    if (direct || byCategory) { out.push(tax.id); }
  }
  return out;
}
